import React, { useContext, useState } from "react";
import { AdminStateContext } from "../../../../providers/AdminStateProvider.jsx";
import { useOracleRequests } from "../hooks/useOracleRequests";
import RequestList from "../components/OracleSync/RequestList";
import RequestDetailsModal from "../components/OracleSync/RequestDetailsModal";
import SyncTriggerForm from "../components/OracleSync/SyncTriggerForm";
import SyncHeader from "../components/SyncHeader/SyncHeader.jsx";
import styles from "./OracleSync.module.css";

// Constants for statuses to maintain stable references
const ACTIVE_STATUSES = ['PENDING', 'PROCESSING'];
const COMPLETED_STATUSES = ['COMPLETED', 'FAILED'];

const OracleSync = () => {
    const [adminState] = useContext(AdminStateContext);
    const { country } = adminState;
    const [selectedRequestId, setSelectedRequestId] = useState(null);

    // Hook for Active Requests (Fast Polling)
    const {
        requests: activeRequests,
        loading: activeLoading,
        refetch: refetchActive
    } = useOracleRequests({
        country,
        statuses: ACTIVE_STATUSES,
        limit: 10,
        interval: 10000,
        isEnabled: true
    });

    // Hook for Completed Requests (Slow Polling)
    const {
        requests: completedRequests,
        loading: completedLoading
    } = useOracleRequests({
        country,
        statuses: COMPLETED_STATUSES,
        limit: 10,
        interval: 30000,
        isEnabled: true
    });

    // Callback when sync is successfully triggered
    const handleSyncSuccess = () => {
        // Immediately refresh the active requests list
        refetchActive();
    };

    // Handle clicking on a request to view details
    const handleRequestClick = (requestId) => {
        setSelectedRequestId(requestId);
    };

    // Close the details modal
    const handleCloseModal = () => {
        setSelectedRequestId(null);
    };

    return (
        <>
            <SyncHeader>
                <SyncHeader.TitleSection>
                    <SyncHeader.Title>Sincronización Oracle</SyncHeader.Title>
                    <SyncHeader.Subtitle>
                        Carga de asientos contables a Oracle
                    </SyncHeader.Subtitle>
                </SyncHeader.TitleSection>
            </SyncHeader>

            <div className={styles.pageContainer}>
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Últimos Cargados</h2>
                    <div className={styles.descriptionBox}>
                        Cada solicitud agrupa los movimientos que enviaste para ser procesados o reprocesados. El Request es el identificador de esa solicitud y te permite seguir su estado, validar resultados y generar nuevamente reportes si lo necesitas. Te recomendamos guardar este identificador para poder relacionarlo fácilmente con los movimientos enviados y mantener el orden de tus procesos.
                    </div>
                    {completedLoading && !completedRequests.length ? (
                        <div>Cargando...</div>
                    ) : (
                        <RequestList
                            requests={completedRequests}
                            showProgress={false}
                            onRequestClick={handleRequestClick}
                        />
                    )}
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Procesos en progreso</h2>
                    <div className={styles.descriptionBox}>
                        El sistema puede procesar hasta 3 solicitudes al mismo tiempo. Si envías más solicitudes, estas quedarán en espera y se procesarán automáticamente a medida que finalicen las anteriores.
                    </div>
                    {activeLoading && !activeRequests.length ? (
                        <div>Cargando...</div>
                    ) : (
                        <RequestList
                            requests={activeRequests}
                            showProgress={true}
                            onRequestClick={handleRequestClick}
                        />
                    )}
                </div>

                <div className={styles.section}>
                    <SyncTriggerForm onSuccess={handleSyncSuccess}>
                        <SyncTriggerForm.Header />
                        <SyncTriggerForm.InputArea />
                        <SyncTriggerForm.SubmitButton />
                        <SyncTriggerForm.StatusSection />
                    </SyncTriggerForm>
                </div>
            </div>

            <RequestDetailsModal
                isOpen={selectedRequestId !== null}
                onClose={handleCloseModal}
                requestId={selectedRequestId}
                country={country}
            />
        </>
    );
};

export default OracleSync;
